'use client';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { convexQuery } from '@convex-dev/react-query';
import { api } from '@/lib/convex';
import type { createNavigationDeliveryStore } from '@/components/home/design-composer/navigation-delivery-store';
import { navigationReconcileInputs } from '@/components/home/design-composer/navigation-delivery-reconcile';

/**
 * Settle entries this instance cannot observe (restored after a reload or
 * remount, or failed) against the actor's server receipts. The subscription
 * is reactive, so a send that commits after the check still clears its entry.
 */
export function useNavigationDeliveryReconciliation(
  store: ReturnType<typeof createNavigationDeliveryStore>,
  actorId: string | null | undefined
) {
  const entries = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
  const inputs = useMemo(
    () => navigationReconcileInputs(entries, actorId),
    [entries, actorId]
  );
  const { data, error } = useQuery(
    convexQuery(
      api.organizations.threads.send_receipts
        .listAcceptedSendInputsForCurrentUser,
      actorId && inputs.length ? { inputs } : 'skip'
    )
  );
  useEffect(() => {
    if (!actorId || !inputs.length) return;
    const checked = inputs.map((input) => input.clientInputId);
    if (data) store.reconcile(actorId, checked, data);
    else if (error) store.failUnconfirmed(actorId, checked);
  }, [actorId, data, error, inputs, store]);
}
